import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import CountrySelect from "aldoo-ra/CountrySelect"
import { showNotification } from "aldoo-ra/Notification"
import { getBillingDetails, updateBillingDetails } from "aldoo-ra/Store/api"
import { usePromise } from "aldoo-ra/usePromise"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { motion } from "framer-motion"

const BillingDetailsCard = ({ isExpanded, onToggle }) => {
  const [billingData, setBillingData] = useState({
    companyName: "",
    taxId: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    },
  })

  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)

  const texts = TextBundle({ id: "aldoo-store" })

  const {
    result: loadedData,
    execute,
    pending: loading,
  } = usePromise(getBillingDetails, false)

  useEffect(() => {
    if (loadedData) {
      setBillingData({
        companyName: loadedData.companyName || "",
        taxId: loadedData.taxId || "",
        address: {
          line1: loadedData.address?.line1 || "",
          line2: loadedData.address?.line2 || "",
          city: loadedData.address?.city || "",
          state: loadedData.address?.state || "",
          postal_code: loadedData.address?.postal_code || "",
          country: loadedData.address?.country || "",
        },
      })
    }
  }, [loadedData])

  const handleInputChange = (field) => (e) => {
    const value = e.target.value
    if (field.startsWith("address.")) {
      const addressField = field.split(".")[1]
      setBillingData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }))
    } else {
      setBillingData((prev) => ({
        ...prev,
        [field]: value,
      }))
    }
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await updateBillingDetails(billingData)

      showNotification({
        message: "Billing details updated successfully",
        className: "bg-primary text-white",
      })

      setHasChanges(false)
      // Reload to confirm saved data
      await execute()
    } catch (error) {
      console.error("Failed to update billing details:", error)
      showNotification({
        message: error?.message || "Failed to update billing details",
        className: "bg-error text-white",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="account-card shadow-custom_card relative">
      {/* Chevron Toggle - Top Right Corner */}
      <button
        onClick={onToggle}
        className="absolute top-4 right-4 p-1 hover:bg-gray_2/20 rounded-full transition-colors z-10"
      >
        <ChevronDownIcon
          className={`w-6 h-6 text-text transition-transform duration-300 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Loading Spinner */}
      {loading && (
        <div className="absolute top-4 right-14 z-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Title */}
      <Typography 
        variant="h5" 
        className="text-text mt-0 mb-7"
      >
        {texts?.billing_info_title || "Billing Information"}
      </Typography>

      {/* Expandable Content */}
      <motion.div
        initial={{ height: "auto", opacity: 1 }}
        animate={{ height: isExpanded ? "auto" : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden w-full"
      >
        <div className="w-full space-y-[30px]">
          {/* Company Name Input */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_company_name || "Company Name"}
            </Typography>

            <Input
              value={billingData.companyName}
              onChange={handleInputChange("companyName")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_company_placeholder || "Your company name"}
            />
          </div>

          {/* VAT/Tax ID Input */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_vat || "VAT / Tax ID"}
            </Typography>

            <Input
              value={billingData.taxId}
              onChange={handleInputChange("taxId")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_vat_placeholder || "e.g., GB123456789"}
            />
          </div>

          {/* Address Line 1 */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_address_line1 || "Address Line 1"}
            </Typography>

            <Input
              value={billingData.address.line1}
              onChange={handleInputChange("address.line1")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_address_line1_placeholder || "Street address"}
            />
          </div>

          {/* Address Line 2 */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_address_line2 || "Address Line 2 (Optional)"}
            </Typography>

            <Input
              value={billingData.address.line2}
              onChange={handleInputChange("address.line2")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_address_line1_placeholder || "Apartment, suite, etc."}
            />
          </div>

          {/* City */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_city || "City"}
            </Typography>

            <Input
              value={billingData.address.city}
              onChange={handleInputChange("address.city")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_city_placeholder || "City"}
            />
          </div>

          {/* State/Province */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_state || "State / Province"}
            </Typography>

            <Input
              value={billingData.address.state}
              onChange={handleInputChange("address.state")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_state_placeholder || "State or province"}
            />
          </div>

          {/* Postal Code */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_postal_code || "Postal Code"}
            </Typography>

            <Input
              value={billingData.address.postal_code}
              onChange={handleInputChange("address.postal_code")}
              variant="aqua_input"
              className="w-full rounded-full bg-theme_white text-text shadow-none"
              placeholder={texts?.billing_info_postal_placeholder || "Postal Code"}
            />
          </div>

          {/* Country */}
          <div className="space-y-2">
            <Typography 
              variant="info_18_accent" 
              className="text-text my-0"
            >
              {texts?.billing_info_country || "Country"}
            </Typography>

            <CountrySelect
              value={billingData.address.country}
              onChange={(countryCode) => {
                setBillingData((prev) => ({
                  ...prev,
                  address: {
                    ...prev.address,
                    country: countryCode,
                  },
                }))
                setHasChanges(true)
              }}
              variant="aqua_input"
              placeholder={texts?.billing_info_country_placeholder || "Select country"}
              className="shadow-none"
              enableResetButton={false}
            />
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              variant="primary_s"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? texts?.billing_info_cta_saving || "Saving..." : texts?.billing_info_cta_save || "Save Changes"}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default BillingDetailsCard
